/**
 * 文件定位：
 * 1. 当前文件位于 web-client/views/editor；
 * 2. 负责把 SolidWorks 风格的子装配体删除范围选择接入 NaiveUI dialog；
 * 3. 该入口只解析用户意图，不执行 working 文档或场景删除。
 */

import { h } from 'vue';
import AssemblyComponentDeleteScopeDialog from '@/components/dialog/AssemblyComponentDeleteScopeDialog.vue';

const STORAGE_KEY = 'puppycad.assembly-component-delete-scope';

/**
 * 装配组件删除范围。
 */
export type AssemblyComponentDeleteScope =
    | 'delete-subassembly'
    | 'delete-selected-component';

/**
 * 装配删除范围选择对话框端口。
 */
export interface AssemblyComponentDeleteScopeDialogPort {
    /** 创建对话框。 */
    readonly create: (options: Record<string, unknown>) => unknown;
}

/**
 * 装配删除范围选择请求。
 */
export interface RequestAssemblyComponentDeleteScopeSelectionInput {
    /** NaiveUI dialog 实例。 */
    readonly dialog: AssemblyComponentDeleteScopeDialogPort;
}

/**
 * 装配删除范围选择结果。
 */
export interface AssemblyComponentDeleteScopeSelectionResult {
    /** 用户选择的删除范围；取消时为 null。 */
    readonly scope: AssemblyComponentDeleteScope | null;
    /** 是否来自本地“不再显示”偏好。 */
    readonly fromPreference: boolean;
}

/**
 * 判断删除范围是否是可持久化的合法值。
 *
 * @param value 待检查值。
 * @returns 合法删除范围返回 true。
 */
function isAssemblyComponentDeleteScope(value: unknown): value is AssemblyComponentDeleteScope {
    return value === 'delete-subassembly' || value === 'delete-selected-component';
}

/**
 * 读取本地保存的删除范围偏好。
 *
 * @returns 已保存的删除范围；不存在或不可用时返回 null。
 */
function readPersistedScopePreference(): AssemblyComponentDeleteScope | null {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        const value = window.localStorage.getItem(STORAGE_KEY);
        return isAssemblyComponentDeleteScope(value) ? value : null;
    } catch (error) {
        console.warn('[AssemblyComponentDeleteScope] 读取删除范围偏好失败', error);
        return null;
    }
}

/**
 * 保存用户选择的删除范围偏好。
 *
 * @param scope 需要保存的删除范围。
 */
function writePersistedScopePreference(scope: AssemblyComponentDeleteScope): void {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        window.localStorage.setItem(STORAGE_KEY, scope);
    } catch (error) {
        console.warn('[AssemblyComponentDeleteScope] 保存删除范围偏好失败', error);
    }
}

/**
 * 请求用户选择装配组件删除范围。
 *
 * 设计说明：
 * 1. 当删除目标位于子装配体内部时，SolidWorks 会先让用户选择删除包含它的子装配体，
 *    或仅删除选定零部件；
 * 2. 该函数只返回用户选择，后续仍必须进入统一确认删除弹窗；
 * 3. 勾选“不要再显示”时仅保存删除范围偏好，不执行任何删除动作。
 *
 * @param input 删除范围选择请求。
 * @returns 用户选择结果。
 */
export function requestAssemblyComponentDeleteScopeSelection(
    input: RequestAssemblyComponentDeleteScopeSelectionInput,
): Promise<AssemblyComponentDeleteScopeSelectionResult> {
    const persistedScope = readPersistedScopePreference();
    if (persistedScope) {
        return Promise.resolve({
            scope: persistedScope,
            fromPreference: true,
        });
    }

    return new Promise((resolve) => {
        let resolved = false;
        let dialogReactive: { destroy?: () => void } | null = null;

        /**
         * 对话框可能从选项、取消或关闭图标结束，统一收口避免重复 resolve。
         */
        const finish = (scope: AssemblyComponentDeleteScope | null): void => {
            if (resolved) {
                return;
            }
            resolved = true;
            resolve({
                scope,
                fromPreference: false,
            });
        };

        dialogReactive = input.dialog.create({
            title: '删除',
            content: () => h(AssemblyComponentDeleteScopeDialog, {
                onSelect: (scope: AssemblyComponentDeleteScope, doNotShowAgain: boolean = false) => {
                    if (doNotShowAgain) {
                        writePersistedScopePreference(scope);
                    }
                    dialogReactive?.destroy?.();
                    finish(scope);
                },
            }),
            negativeText: '取消',
            showIcon: false,
            onNegativeClick: () => {
                finish(null);
            },
            onClose: () => {
                finish(null);
            },
        }) as { destroy?: () => void };
    });
}
